import React, { useEffect, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useDispatch } from 'react-redux';
import { format, parseISO } from 'date-fns';
import Icon from 'react-native-vector-icons/Ionicons';

import { AppDispatch } from '@/store';
import { addMissingPunch } from '@/store/slices/timeClockSlice';
import { colors, font, space, radius } from '@/theme/chalkCourt';
import {
  isValidTime24,
  minutesBetweenTimes,
  stepQuarterHour,
  time24ToDisplay,
} from '@/utils/timeParser';
import TimeField from '@/components/TimeField';

export type MissingMode = 'in' | 'out' | 'both';

/**
 * Opening the sheet for a specific gap — e.g. a clock-in on Thu with no
 * clock-out. Locks the day and the punch type; `otherTime` is the punch already
 * on record (the new one must land on the correct side of it).
 */
export interface MissingPunchTarget {
  /** yyyy-MM-dd */
  date: string;
  mode: 'in' | 'out';
  /** HH:mm of the existing half of the pair. */
  otherTime: string;
  /**
   * The gap sits between two punches (a double): the new time must also be
   * before this HH:mm (missing clock-out) / after it (missing clock-in).
   */
  bound?: string;
}

interface Props {
  visible: boolean;
  onClose: () => void;
  /** Called with the backend's confirmation after a successful send. */
  onSubmitted: (message: string) => void;
  /** A specific gap from the timesheet; omit for the general "add a punch" form. */
  target?: MissingPunchTarget | null;
  /** Days the staffer may pick from (yyyy-MM-dd, newest first) — the pay period so far. */
  days: string[];
}

const MODES: { key: MissingMode; label: string }[] = [
  { key: 'in', label: 'Clock in' },
  { key: 'out', label: 'Clock out' },
  { key: 'both', label: 'Both' },
];

const forgotReason = (mode: MissingMode) =>
  mode === 'in' ? 'Forgot to clock in' : mode === 'out' ? 'Forgot to clock out' : 'Forgot to clock in and out';

const OTHER_REASONS = ['Phone died', "App wasn't working", 'Was with a class'];

const dayLabel = (iso: string, todayIso: string): string => {
  if (iso === todayIso) return 'Today';
  const d = parseISO(iso);
  return format(d, 'EEE d');
};

const fmtLength = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

/**
 * Add a forgotten clock-in and/or clock-out. Written PENDING for a manager to
 * approve (the same as a staff-entered punch in iflipweb2), so it shows on the
 * timesheet right away and counts toward pay once approved.
 */
const AddMissingPunchModal: React.FC<Props> = ({ visible, onClose, onSubmitted, target, days }) => {
  const dispatch = useDispatch<AppDispatch>();
  const todayIso = format(new Date(), 'yyyy-MM-dd');

  const [date, setDate] = useState('');
  const [mode, setMode] = useState<MissingMode>('in');
  const [inTime, setInTime] = useState('');
  const [outTime, setOutTime] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Re-seed each time the sheet opens (it may open for a different gap).
  useEffect(() => {
    if (!visible) return;
    setDate(target?.date ?? days[0] ?? todayIso);
    setMode(target?.mode ?? 'in');
    setInTime('');
    setOutTime('');
    setReason('');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, target?.date, target?.mode, target?.otherTime, target?.bound]);

  const wantsIn = mode === 'in' || mode === 'both';
  const wantsOut = mode === 'out' || mode === 'both';

  const validate = (): string | null => {
    if (!date) return 'Pick the day.';
    if (wantsIn && !isValidTime24(inTime)) return 'Enter the clock-in time, like 9:00 AM.';
    if (wantsOut && !isValidTime24(outTime)) return 'Enter the clock-out time, like 5:30 PM.';
    if (mode === 'both' && (minutesBetweenTimes(inTime, outTime) ?? 0) <= 0) {
      return 'The clock-out has to be after the clock-in.';
    }
    if (target?.mode === 'out' && (minutesBetweenTimes(target.otherTime, outTime) ?? 0) <= 0) {
      return `The clock-out has to be after your ${time24ToDisplay(target.otherTime)} clock-in.`;
    }
    if (target?.mode === 'in' && (minutesBetweenTimes(inTime, target.otherTime) ?? 0) <= 0) {
      return `The clock-in has to be before your ${time24ToDisplay(target.otherTime)} clock-out.`;
    }
    if (target?.bound && target.mode === 'out' && (minutesBetweenTimes(outTime, target.bound) ?? 0) <= 0) {
      return `The clock-out has to be before your ${time24ToDisplay(target.bound)} clock-in.`;
    }
    if (target?.bound && target.mode === 'in' && (minutesBetweenTimes(target.bound, inTime) ?? 0) <= 0) {
      return `The clock-in has to be after your ${time24ToDisplay(target.bound)} clock-out.`;
    }
    if (date === todayIso) {
      const nowHm = format(new Date(), 'HH:mm');
      const latest = wantsOut ? outTime : inTime;
      if (latest > nowHm) return "That time hasn't happened yet — use Clock in / out for now.";
    }
    if (!reason.trim()) return 'Add a reason for your manager.';
    return null;
  };

  const submit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    setBusy(true);
    const result = await dispatch(
      addMissingPunch({
        date,
        ...(wantsIn ? { clockIn: inTime } : {}),
        ...(wantsOut ? { clockOut: outTime } : {}),
        note: reason.trim(),
      })
    );
    setBusy(false);
    if (addMissingPunch.fulfilled.match(result) && result.payload.success) {
      onSubmitted(result.payload.message);
      onClose();
    } else {
      const msg = addMissingPunch.fulfilled.match(result)
        ? result.payload.message
        : (result.payload as string);
      setError(msg || "Couldn't send the punch. Try again.");
    }
  };

  const when = target ? format(parseISO(target.date), 'EEE, MMM d') : '';
  const context = target?.bound
    ? target.mode === 'out'
      ? `You clocked in at ${time24ToDisplay(target.otherTime)} and again at ${time24ToDisplay(target.bound)} on ${when}. When did you clock out in between?`
      : `You clocked out at ${time24ToDisplay(target.bound)} and again at ${time24ToDisplay(target.otherTime)} on ${when}. When did you clock back in?`
    : target
    ? target.mode === 'out'
      ? `You clocked in at ${time24ToDisplay(target.otherTime)} on ${format(parseISO(target.date), 'EEE, MMM d')} with no clock-out. When did you leave?`
      : `You clocked out at ${time24ToDisplay(target.otherTime)} on ${format(parseISO(target.date), 'EEE, MMM d')} with no clock-in. When did you start?`
    : 'Forgot to clock in or out? Add the time you missed.';

  // What the entered time(s) add up to — feedback that the entry was understood.
  const pair: [string, string] | null =
    mode === 'both'
      ? [inTime, outTime]
      : target?.mode === 'out'
        ? [target.otherTime, outTime]
        : target?.mode === 'in'
          ? [inTime, target.otherTime]
          : null;
  const length = pair ? minutesBetweenTimes(pair[0], pair[1]) : null;
  // − / + on an empty field start next to the punch already on record.
  const outStart = target?.mode === 'out' ? stepQuarterHour(target.otherTime, 1) : undefined;
  const inStart =
    target?.mode === 'in'
      ? stepQuarterHour(target.bound ?? target.otherTime, target.bound ? 1 : -1)
      : undefined;
  const reasons = [forgotReason(mode), ...OTHER_REASONS];

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <KeyboardAvoidingView
        style={styles.backdrop}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <View style={styles.sheet}>
          <View style={styles.handle} />
          <View style={styles.headRow}>
            <Text style={styles.title}>ADD MISSING PUNCH</Text>
            <TouchableOpacity
              onPress={onClose}
              accessibilityLabel="close"
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
              <Icon name="close" size={24} color={colors.inkSoft} />
            </TouchableOpacity>
          </View>
          <Text style={styles.subtitle}>{context}</Text>

          <ScrollView keyboardShouldPersistTaps="handled" style={styles.form}>
            <Text style={styles.label}>DAY</Text>
            {target ? (
              <View style={[styles.input, styles.inputLocked]}>
                <Text style={styles.lockedText}>{format(parseISO(target.date), 'EEEE, MMM d')}</Text>
              </View>
            ) : (
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.chips}>
                  {days.map((d) => {
                    const on = d === date;
                    return (
                      <TouchableOpacity
                        key={d}
                        style={[styles.chip, on && styles.chipOn]}
                        activeOpacity={0.8}
                        accessibilityState={{ selected: on }}
                        onPress={() => setDate(d)}>
                        <Text style={[styles.chipText, on && styles.chipTextOn]}>
                          {dayLabel(d, todayIso)}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </ScrollView>
            )}

            {!target && (
              <>
                <Text style={styles.label}>WHAT DID YOU MISS?</Text>
                <View style={styles.segment}>
                  {MODES.map((m) => {
                    const on = m.key === mode;
                    return (
                      <TouchableOpacity
                        key={m.key}
                        style={[styles.segmentBtn, on && styles.segmentBtnOn]}
                        activeOpacity={0.8}
                        accessibilityState={{ selected: on }}
                        onPress={() => setMode(m.key)}>
                        <Text style={[styles.segmentText, on && styles.segmentTextOn]}>{m.label}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </>
            )}

            {wantsIn && (
              <TimeField label="CLOCK IN" value={inTime} onChange={setInTime} emptyStart={inStart} />
            )}
            {wantsOut && (
              <TimeField
                label="CLOCK OUT"
                value={outTime}
                onChange={setOutTime}
                defaultPeriod="PM"
                emptyStart={outStart}
              />
            )}
            {pair && length !== null && (
              <Text style={[styles.length, length <= 0 && styles.lengthBad]}>
                {length > 0
                  ? `${time24ToDisplay(pair[0])} – ${time24ToDisplay(pair[1])} · ${fmtLength(length)} worked`
                  : mode === 'both' || target?.mode === 'out'
                    ? 'The clock-out is before the clock-in'
                    : 'The clock-in is after the clock-out'}
              </Text>
            )}

            <Text style={styles.label}>REASON</Text>
            <View style={styles.chipsWrap}>
              {reasons.map((r) => {
                const on = reason === r;
                return (
                  <TouchableOpacity
                    key={r}
                    style={[styles.chip, on && styles.chipOn]}
                    activeOpacity={0.8}
                    onPress={() => setReason(r)}>
                    <Text style={[styles.chipText, on && styles.chipTextOn]}>{r}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <TextInput
              style={[styles.input, styles.inputMultiline]}
              value={reason}
              onChangeText={setReason}
              placeholder="Anything your manager should know"
              placeholderTextColor={colors.inkFaint}
              accessibilityLabel="Reason"
              multiline
            />

            {error && (
              <View style={styles.errorBox}>
                <Icon name="alert-circle" size={16} color={colors.accentDark} />
                <Text style={styles.errorText}>{error}</Text>
              </View>
            )}
          </ScrollView>

          <TouchableOpacity
            style={[styles.submit, busy && styles.submitBusy]}
            activeOpacity={0.85}
            disabled={busy}
            onPress={submit}>
            <Text style={styles.submitText}>{busy ? 'ADDING…' : 'ADD PUNCH'}</Text>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(28,26,18,0.45)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.bg,
    borderTopLeftRadius: radius.lg,
    borderTopRightRadius: radius.lg,
    paddingHorizontal: space.lg,
    paddingBottom: space.xl,
    maxHeight: '92%',
  },
  handle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.lineStrong,
    marginTop: space.sm,
    marginBottom: space.md,
  },
  headRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontFamily: font.display,
    fontSize: 30,
    color: colors.ink,
    letterSpacing: -0.5,
  },
  subtitle: {
    fontFamily: font.body,
    fontSize: 13.5,
    lineHeight: 19,
    color: colors.inkSoft,
    marginTop: 2,
    marginBottom: space.md,
  },
  form: {
    flexGrow: 0,
  },
  label: {
    fontFamily: font.bodyBold,
    fontSize: 11,
    letterSpacing: 1.6,
    color: colors.inkFaint,
    marginBottom: space.xs,
    marginTop: space.md,
  },
  input: {
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    paddingHorizontal: space.md,
    paddingVertical: space.md,
    fontFamily: font.bodyMed,
    fontSize: 16,
    color: colors.ink,
  },
  inputLocked: {
    backgroundColor: colors.surfaceSunk,
  },
  lockedText: {
    fontFamily: font.bodyMed,
    fontSize: 16,
    color: colors.inkSoft,
  },
  inputMultiline: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  chips: {
    flexDirection: 'row',
    gap: space.xs,
  },
  chipsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: space.xs,
    marginBottom: space.sm,
  },
  chip: {
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.line,
    backgroundColor: colors.surface,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  chipOn: {
    borderColor: colors.accent,
    backgroundColor: colors.accentSoft,
  },
  chipText: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.inkSoft,
  },
  chipTextOn: {
    color: colors.accentDark,
  },
  segment: {
    flexDirection: 'row',
    padding: 3,
    gap: 3,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    backgroundColor: colors.surfaceSunk,
  },
  segmentBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: radius.sm,
    alignItems: 'center',
  },
  segmentBtnOn: {
    backgroundColor: colors.ink,
  },
  segmentText: {
    fontFamily: font.bodySemi,
    fontSize: 14,
    color: colors.inkSoft,
  },
  segmentTextOn: {
    color: colors.surface,
  },
  length: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.inkSoft,
    marginTop: space.sm,
  },
  lengthBad: {
    color: colors.accentDark,
  },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderRadius: radius.sm,
    padding: space.sm,
    marginTop: space.md,
  },
  errorText: {
    flex: 1,
    marginLeft: space.xs,
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.accentDark,
  },
  submit: {
    height: 56,
    borderRadius: radius.md,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: space.lg,
  },
  submitBusy: {
    opacity: 0.5,
  },
  submitText: {
    fontFamily: font.display,
    fontSize: 22,
    letterSpacing: 1,
    color: colors.onAccent,
  },
});

export default AddMissingPunchModal;
